import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import { api, buildWhatsAppLink, WHATSAPP_DISPLAY, resolveImg } from "../lib/api";
import Seo from "../components/Seo";

const PORTRAIT = resolveImg("/api/uploads/portrait_emmanuel.jpg");

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (_e) {
      setErr("No pudimos enviar el mensaje. Escríbeme por WhatsApp directamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="about-page" className="pt-[18vh] sm:pt-[22vh] pb-[10vh]">
      <Seo
        title="Sobre mí — Fotógrafo de la cultura"
        description="Emmanuel Upegui · 15+ años narrando proyectos musicales, artísticos y culturales en Colombia, México, Perú, España, Estados Unidos, Alemania y China. Magíster en periodismo y estratega de comunicaciones."
        path="/sobre-mi"
        type="profile"
        keywords="biografía Emmanuel Upegui, fotógrafo Colombia, magíster periodismo, estratega comunicaciones, fotógrafo cultural"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "Sobre Emmanuel Upegui",
          "url": "https://www.emmanuelupegui.com/sobre-mi",
          "mainEntity": {
            "@type": "Person",
            "name": "Emmanuel Upegui",
            "jobTitle": "Fotógrafo de la cultura",
            "url": "https://www.emmanuelupegui.com/",
          },
        }}
      />
      <header className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw]">
        <p className="text-[11px] tracking-[0.4em] uppercase font-display text-[var(--magenta)]">Sobre mí</p>
        <h1 className="font-display uppercase font-bold tracking-tighter leading-[0.85] text-[16vw] sm:text-[12vw] lg:text-[10vw] mt-3">
          Emmanuel <span className="text-[var(--magenta)] normal-case">Upegui</span>
        </h1>
      </header>

      {/* CHAPTER 1 — fotógrafo de la cultura */}
      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-16 sm:mt-24 grid grid-cols-12 gap-8 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.9 }}
          className="col-span-12 md:col-span-5"
        >
          <div className="aspect-[4/5] overflow-hidden bg-[var(--surface)] stage-glow-magenta">
            <img
              src={PORTRAIT}
              alt="Retrato de Emmanuel Upegui — fotógrafo de la cultura"
              className="w-full h-full object-cover"
              style={{ objectPosition: "38% center" }}
            />
          </div>
          <p className="mt-4 font-mono text-[11px] tracking-[0.3em] uppercase text-white/40">
            Base en Colombia · Disponible en Latinoamérica, Europa y Asia
          </p>
        </motion.div>

        <div className="col-span-12 md:col-span-7 space-y-8">
          <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--cyan)]">01 · Fotógrafo de la cultura</p>
          <p className="font-serif-accent text-2xl sm:text-3xl leading-[1.3] text-white/90 font-light">
            Soy fotógrafo y me dedico a la cultura. Muy seguramente, porque aún me siento
            <span className="text-white"> hechizado</span> por las cosas que de niño no logré descifrar,
            pero que me atraparon — <em className="not-italic text-[var(--magenta)]">de por vida</em>.
          </p>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg">
            Tengo más de 15 años de experiencia narrando proyectos musicales, artísticos y culturales en
            Colombia, México, Perú, España, Estados Unidos, Alemania y China. He trabajado con músicos y
            agrupaciones, con artistas visuales, con instituciones públicas y privadas, para festivales,
            para sellos, para colectivos — y para mí mismo.
          </p>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg">
            También soy <span className="text-white">estratega de comunicaciones</span> con los mismos
            años de experiencia en instituciones de las industrias culturales y creativas. Soy magíster
            en periodismo, por lo que me encanta contar historias.
          </p>
        </div>
      </section>

      {/* CHAPTER 2 — tomo fotos y cuento historias */}
      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-24 sm:mt-32 grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 md:col-span-5">
          <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--magenta)]">02 · Tomo fotos y cuento historias</p>
        </div>
        <div className="col-span-12 md:col-span-7 space-y-6">
          <p className="font-serif-accent text-xl sm:text-2xl leading-[1.4] text-white/85 font-light">
            Pero más allá del tomar una foto o contar una historia — lo cual puede obedecer a razones
            meramente estratégicas, de mercado seguramente — me pregunto el
            <span className="text-[var(--magenta)]"> por qué</span> cada vez que lo hago.
          </p>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg">
            ¿Por qué una foto tuya o de los artistas y creadores a los que representas? ¿Qué le quieres
            decir al mundo cuando estás sobre la tarima, cuando grabas, cuando lanzas un proyecto, cuando
            expones, cuando publicas?
          </p>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg">
            Mi propósito es ayudar a desarrollar esa voz — la del artista, la del creador, la de la
            institución que cree en la cultura como forma de transformar — y contribuir a la conexión
            con su público. Sumar en la búsqueda de la diferenciación. A la construcción de un proyecto
            <span className="text-white"> sostenible, con sentido y espíritu</span>. Al fin y al cabo,
            es arte — y también negocio.
          </p>
        </div>
      </section>

      {/* CHAPTER 3 — y lo amo */}
      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-24 sm:mt-32 grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 md:col-span-5">
          <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--tungsten)]">03 · Y lo amo</p>
        </div>
        <div className="col-span-12 md:col-span-7 space-y-6">
          <p className="font-serif-accent text-xl sm:text-2xl leading-[1.4] text-white/85 font-light">
            Amo lo que hago. Porque para mí la cultura y la fotografía son
            <span className="text-white"> formas inacabables de buscarle sentido al mundo</span>.
            De enfrentar lo que desconoces y aceptar que lo desconoces.
          </p>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg">
            De descubrir nuevas formas de existir cada vez que encuentras una obra que te vuela la cabeza
            — como bien le pasó al niño que escuchaba los toms de una batería por primera vez a los 11
            años, y que desde entonces <em className="not-italic text-[var(--magenta)]">no ha parado de mirar</em>.
          </p>
        </div>
      </section>

      {/* STATS — full-width mural, big and breathing */}
      <section
        data-testid="stats-mural"
        className="relative mt-28 sm:mt-36 border-y border-white/5"
      >
        <div className="max-w-[1700px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] py-16 sm:py-24">
          <div className="flex items-center gap-3 mb-10 sm:mb-14">
            <span className="block w-8 h-px bg-[var(--magenta)]" />
            <p className="font-mono text-[10px] sm:text-[11px] tracking-[0.4em] uppercase text-white/45">
              Cifras del oficio
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-14 lg:gap-y-0 lg:gap-x-12 xl:gap-x-20">
            <Stat label="Años en oficio" value="15+" accent="var(--magenta)" />
            <Stat label="Países" value="07" accent="var(--cyan)" />
            <Stat label="Proyectos publicados" value="40+" accent="var(--tungsten)" />
            <Stat label="Festivales cubiertos" value="20+" accent="var(--magenta)" />
          </div>
        </div>
      </section>

      {/* PARA QUIÉN */}
      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-24 sm:mt-32">
        <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--cyan)]">Para quién</p>
        <h2 className="font-display uppercase font-medium tracking-tighter leading-[0.92] text-3xl sm:text-5xl lg:text-6xl mt-4 max-w-4xl">
          Bandas. Solistas. Festivales. Instituciones. Marcas. Medios.
        </h2>
        <p className="mt-6 max-w-2xl text-white/70 leading-relaxed">
          Sesiones de banda y artistas. Cobertura de conciertos y festivales. Reportaje cultural y editorial.
          Comisiones para industrias creativas. Eventos privados y ceremonias. Trabajo de larga duración.
        </p>
      </section>

      {/* PUBLICADO EN */}
      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-24 sm:mt-32">
        <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-white/40">Publicado en</p>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3 text-white/75 max-w-3xl">
          <li className="link-reveal"><a href="https://semanarural.com/web/articulo/zoom/bolivar/la-tambora-un-viaje-sonoro-por-el-sur-de-bolivar/465" target="_blank" rel="noreferrer">Semana Rural — Tambora</a></li>
          <li className="link-reveal"><a href="https://www.elespectador.com/noticias/noticias-de-cultura/que-volumen-el-de-las-puloy-de-montecristo-articulo-846665" target="_blank" rel="noreferrer">El Espectador — Puloy de Montecristo</a></li>
          <li className="link-reveal"><a href="https://cerosetenta.uniandes.edu.co/universitarios-por-la-paz/" target="_blank" rel="noreferrer">Cerosetenta — Universitarios por la paz</a></li>
          <li className="link-reveal"><a href="https://cerosetenta.uniandes.edu.co/453-semanas-santas-en-mompox/" target="_blank" rel="noreferrer">Cerosetenta — Semanas Santas en Mompox</a></li>
          <li className="link-reveal"><a href="https://www.semana.com/mejor-colombia/articulo/de-pelicula-asi-es-el-bosque-colombiano-que-lo-hara-sentir-en-el-mundo-de-avatar/202418/" target="_blank" rel="noreferrer">Semana — Bosques de Pandora</a></li>
          <li className="link-reveal"><a href="https://cerosetenta.uniandes.edu.co/el-sendero-del-patrullero/" target="_blank" rel="noreferrer">Cerosetenta — El sendero del patrullero</a></li>
        </ul>
      </section>

      {/* CONTACTO */}
      <section id="contacto" className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-32 sm:mt-40 pb-24 grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 md:col-span-5">
          <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--cyan)]">Contacto</p>
          <h2 className="font-display uppercase font-medium tracking-tighter leading-[0.9] text-5xl sm:text-7xl mt-4">
            Sesiones, eventos y <span className="brand-upegui text-[var(--magenta)]">comisiones</span>.
          </h2>
          <p className="mt-6 text-white/70 leading-relaxed">
            Tarifas a la medida según el alcance del proyecto: fotografía de música en vivo, sesiones para bandas,
            cobertura de eventos, reportaje documental y comisiones editoriales. Respuesta en menos de 24 horas.
          </p>
          <div className="mt-10 space-y-3">
            <div>
              <a
                data-testid="about-whatsapp"
                href={buildWhatsAppLink("Hola Emmanuel, vengo de tu portafolio.")}
                target="_blank" rel="noreferrer"
                className="link-reveal text-white"
              >
                WhatsApp · {WHATSAPP_DISPLAY}
              </a>
            </div>
            <div>
              <a className="link-reveal text-white" href="https://instagram.com/e.upegui" target="_blank" rel="noreferrer">
                Instagram · @e.upegui
              </a>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7">
          {sent ? (
            <div data-testid="contact-success" className="border border-[var(--cyan)]/40 p-10 stage-glow-cyan">
              <CheckCircle2 className="text-[var(--cyan)]" size={32} />
              <h3 className="font-display uppercase text-3xl mt-4">Mensaje enviado</h3>
              <p className="text-white/70 mt-2">Gracias. Te responderé pronto.</p>
              <button
                onClick={() => setSent(false)}
                className="mt-6 text-[11px] tracking-[0.3em] uppercase font-display border border-white/20 px-5 py-2.5 hover:border-white"
              >
                Enviar otro
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <Field label="Nombre">
                <input
                  required
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-3 text-lg"
                />
              </Field>
              <Field label="Correo">
                <input
                  required type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-3 text-lg"
                />
              </Field>
              <Field label="Mensaje">
                <textarea
                  required rows={5}
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-3 text-lg resize-none"
                />
              </Field>
              {err && <p className="text-[var(--magenta)] text-sm" data-testid="contact-error">{err}</p>}
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-[var(--magenta)] hover:text-[var(--magenta)] disabled:opacity-50 px-8 py-4 text-[12px] tracking-[0.3em] uppercase font-display transition-colors"
              >
                {sending ? "Enviando…" : "Enviar"} <Send size={14} />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.4em] uppercase text-white/40 font-display">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="group relative overflow-hidden">
      {/* Big value — fills the column, hover reveals subtle shimmer */}
      <p
        className="font-display font-bold tracking-[-0.04em] leading-[0.85] text-[clamp(4.5rem,11vw,11rem)] transition-transform duration-700 group-hover:-translate-y-1"
        style={{ color: accent, textShadow: `0 0 60px ${accent}33` }}
        data-testid="stat-value"
      >
        {value}
      </p>
      <div className="mt-5 flex items-center gap-3">
        <span
          className="block h-px w-10 transition-all duration-700 group-hover:w-20"
          style={{ backgroundColor: accent }}
        />
        <p className="text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-white/55 font-display">
          {label}
        </p>
      </div>
    </div>
  );
}
